import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    FormControlLabel,
    Radio,
    RadioGroup,
    Popper,
    Paper,
    InputAdornment,
    ClickAwayListener,
    TextField,
    Typography
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

const normalizeSearch = (value) => String(value ?? "").trim().toLowerCase();

const CustomDropdown = ({
    onSelectionChange,
    setColorOptions,
    setSizeOptions,
    color,
    size,
    colorOptions = [],
    sizeOptions = []
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [open, setOpen] = useState(false);

    const [selectedColor, setSelectedColor] = useState("");
    const [selectedSize, setSelectedSize] = useState("");

    const [newColor, setNewColor] = useState("");
    const [newSize, setNewSize] = useState("");
    const [colorSearch, setColorSearch] = useState("");
    const [sizeSearch, setSizeSearch] = useState("");

    const normalizedColorSearch = colorOptions.length > 3 ? normalizeSearch(colorSearch) : "";
    const normalizedSizeSearch = sizeOptions.length > 3 ? normalizeSearch(sizeSearch) : "";
    const filteredColorOptions = colorOptions.filter((option) =>
        normalizeSearch(option).includes(normalizedColorSearch)
    );
    const filteredSizeOptions = sizeOptions.filter((option) =>
        normalizeSearch(option).includes(normalizedSizeSearch)
    );

    useEffect(() => {
        if (color) {
            setSelectedColor(color);
            if (!colorOptions.includes(color)) {
                setColorOptions([...colorOptions, color]);
            }
        }

        if (size) {
            setSelectedSize(size);
            if (!sizeOptions.includes(size)) {
                setSizeOptions([...sizeOptions, size]);
            }
        }
    }, [color, size]);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
        if (open) {
            setColorSearch("");
            setSizeSearch("");
        }
        setOpen(!open);
    };

    const handleClose = () => {
        setColorSearch("");
        setSizeSearch("");
        setOpen(false);
    };

    const addColor = () => {
        if (newColor && !colorOptions.includes(newColor)) {
            setColorOptions([...colorOptions, newColor]);
            setSelectedColor(newColor);
            setNewColor("");
            setColorSearch("");
        }
    };

    const addSize = () => {
        if (newSize && !sizeOptions.includes(newSize)) {
            setSizeOptions([...sizeOptions, newSize]);
            setSelectedSize(newSize);
            setNewSize("");
            setSizeSearch("");
        }
    };

    const handleDone = () => {
        if (selectedColor && selectedSize) {
            onSelectionChange({ size: selectedSize, color: selectedColor });
            setColorSearch("");
            setSizeSearch("");
            setOpen(false);
        }
    };

    return (
        <Box>
            <TextField
                placeholder="Select Color / Size"
                value={
                    selectedColor && selectedSize
                        ? `${selectedColor}/${selectedSize}`
                        : ""
                }
                onClick={handleClick}
                fullWidth
                sx={{
                    cursor: "pointer",
                    backgroundColor: "white",
                    height: "unset",
                    border: "1px solid #e7e7e7",
                    borderRadius: "4px !important",
                    "&:hover": { borderColor: "#888" },
                    "& .MuiInputBase-root": {
                        height: "unset",
                        paddingRight: "10px",
                        cursor: "pointer",
                    },
                    "& .MuiInputAdornment-root": {
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                    },
                    "& input": {
                        cursor: "pointer",
                    },
                }}
                InputProps={{
                    readOnly: true,
                    endAdornment: (
                        <InputAdornment position="end">
                            <ArrowDropDownIcon
                                sx={{
                                    transform: open ? "rotate(180deg)" : "rotate(0deg)",
                                    transition: "transform 0.3s ease",
                                }}
                            />
                        </InputAdornment>
                    ),
                }}
            />

            <Popper
                open={open}
                anchorEl={anchorEl}
                placement="bottom-start"
                sx={{ zIndex: 1300 }}
            >
                <ClickAwayListener onClickAway={handleClose}>
                    <Paper elevation={3} sx={{ p: 2, minWidth: 400 }}>
                        <Box display="flex" justifyContent="space-between" gap={3}>
                            {/* Color Column */}
                            <Box width="48%">
                                <Typography fontWeight={600}>Color</Typography>
                                {colorOptions.length > 3 && (
                                    <TextField
                                        size="small"
                                        fullWidth
                                        placeholder="Search colors..."
                                        value={colorSearch}
                                        onChange={(e) => setColorSearch(e.target.value)}
                                        sx={{ mt: 1, mb: 1 }}
                                    />
                                )}
                                <Box sx={{ maxHeight: 150, overflowY: 'auto' }}>
                                    <RadioGroup
                                        value={selectedColor}
                                        onChange={(e) => setSelectedColor(e.target.value)}
                                    >
                                        {filteredColorOptions.map((col) => (
                                            <FormControlLabel
                                                key={col}
                                                value={col}
                                                control={<Radio />}
                                                label={col}
                                            />
                                        ))}
                                        {normalizedColorSearch && filteredColorOptions.length === 0 && (
                                            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                                                No colors found
                                            </Typography>
                                        )}
                                    </RadioGroup>
                                </Box>
                                <TextField
                                    size="small"
                                    placeholder="Add new color"
                                    value={newColor}
                                    onChange={(e) => setNewColor(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && addColor()}
                                    sx={{
                                        height: "42px",
                                        width: '170px !important',
                                        border: "1px solid #e7e7e7",
                                        borderRadius: "4px !important",
                                    }}
                                />
                                <Button onClick={addColor} sx={{ ml: 1, height: "42px" }}>
                                    + Add
                                </Button>
                            </Box>

                            {/* Size Column */}
                            <Box width="48%">
                                <Typography fontWeight={600}>Size</Typography>
                                {sizeOptions.length > 3 && (
                                    <TextField
                                        size="small"
                                        fullWidth
                                        placeholder="Search sizes..."
                                        value={sizeSearch}
                                        onChange={(e) => setSizeSearch(e.target.value)}
                                        sx={{ mt: 1, mb: 1 }}
                                    />
                                )}
                                <Box sx={{ maxHeight: 150, overflowY: 'auto' }}>
                                    <RadioGroup
                                        value={selectedSize}
                                        onChange={(e) => setSelectedSize(e.target.value)}
                                    >
                                        {filteredSizeOptions.map((sz) => (
                                            <FormControlLabel
                                                key={sz}
                                                value={sz}
                                                control={<Radio />}
                                                label={sz}
                                            />
                                        ))}
                                        {normalizedSizeSearch && filteredSizeOptions.length === 0 && (
                                            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                                                No sizes found
                                            </Typography>
                                        )}
                                    </RadioGroup>
                                </Box>
                                <TextField
                                    size="small"
                                    placeholder="Add new size"
                                    value={newSize}
                                    onChange={(e) => setNewSize(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && addSize()}
                                    sx={{
                                        height: "42px",
                                        width: '170px !important',
                                        border: "1px solid #e7e7e7",
                                        borderRadius: "4px !important",
                                    }}
                                />
                                <Button onClick={addSize} sx={{ ml: 1, height: "42px" }}>
                                    + Add
                                </Button>
                            </Box>
                        </Box>

                        <Box display="flex" justifyContent="flex-end" mt={2}>
                            <Button
                                onClick={handleDone}
                                color="primary"
                                variant="contained"
                                disabled={!selectedColor || !selectedSize}
                            >
                                Done
                            </Button>
                        </Box>
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </Box>
    );
};

export default CustomDropdown;
